let deliveryOptionsPincodeDataPromise;

if (!customElements.get('delivery-options')) {
  const IST_OFFSET_MS = 330 * 60 * 1000;

  function normalizePincode(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 6);
  }

  function getIndiaNow() {
    return new Date(Date.now() + IST_OFFSET_MS);
  }

  function addIndiaDays(days) {
    const date = getIndiaNow();
    date.setUTCDate(date.getUTCDate() + days);
    return date;
  }

  function formatIndiaDate(date) {
    const parts = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    }).formatToParts(date);
    const weekday = parts.find((part) => part.type === 'weekday')?.value || '';
    const month = parts.find((part) => part.type === 'month')?.value || '';
    const day = parts.find((part) => part.type === 'day')?.value || '';
    return `${weekday}, ${month} ${day}`;
  }

  function renderCheckIcon() {
    return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="#24B36B"/><path d="M4.2 8.35L6.55 10.7L11.85 5.4" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function renderTruckLine(text) {
    return `<span class="product-delivery-options__result-icon"><svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 6.5H13.5V15.5H1.5V6.5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M13.5 9.5H17.1L20.5 12.9V15.5H13.5V9.5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="6" cy="16.5" r="1.5" fill="currentColor"/><circle cx="16.5" cy="16.5" r="1.5" fill="currentColor"/></svg></span><span class="product-delivery-options__result-text">${text}</span>`;
  }

  function getStorageValue(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setStorageValue(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      return;
    }
  }

  async function loadPincodeData(url) {
    if (!deliveryOptionsPincodeDataPromise) {
      deliveryOptionsPincodeDataPromise = fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load pincode data');
        }
        return response.json();
      });
    }

    return deliveryOptionsPincodeDataPromise;
  }

  function findZone(pincode, zones) {
    const numeric = Number(pincode);
    const zoneOrder = ['Local', 'Metro', 'Zonal', 'National', 'Remote', 'Remote area'];

    for (const zoneName of zoneOrder) {
      const ranges = zones[zoneName] || [];
      for (const range of ranges) {
        if (numeric >= range[0] && numeric <= range[1]) {
          return zoneName;
        }
      }
    }

    return null;
  }

  function getDeliveryRule(pincode, zoneName) {
    const numeric = Number(pincode);
    const isKarnataka = numeric >= 560000 && numeric <= 599999;
    const indiaNow = getIndiaNow();
    const isBefore10am = indiaNow.getUTCHours() < 10;

    if (zoneName === 'Local') {
      return {
        days: isBefore10am ? 0 : 1,
        title: isBefore10am ? 'Same day delivery' : 'Next day delivery',
        note: isBefore10am ? 'Order before 10:00 AM' : 'Order after 10:00 AM'
      };
    }

    if (isKarnataka) {
      return {
        days: 2,
        title: 'Karnataka delivery',
        note: 'Estimated in 2 days'
      };
    }

    if (zoneName === 'Metro') {
      return {
        days: 3,
        title: 'Metro delivery',
        note: 'Estimated in 3 days'
      };
    }

    return {
      days: 5,
      title: zoneName ? `${zoneName} delivery` : 'Delivery',
      note: 'Estimated in 5 days'
    };
  }

  async function fetchPostalFromIp() {
    const endpoints = ['https://ipapi.co/json/', 'https://ipwho.is/'];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, { cache: 'no-store' });
        if (!response.ok) {
          continue;
        }

        const data = await response.json();
        const postal = normalizePincode(data.postal || data.zip || data.postal_code || data.zipcode);
        if (postal) {
          return {
            postal,
            source: endpoint,
            city: data.city || '',
            region: data.region || data.state || ''
          };
        }
      } catch (error) {
        continue;
      }
    }

    return null;
  }

  class DeliveryOptions extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';
      this.form = this.querySelector('[data-delivery-form]');
      this.input = this.querySelector('[data-delivery-input]');
      this.validation = this.querySelector('[data-delivery-validation]');
      this.result = this.querySelector('[data-delivery-result]');
      this.pincodeUrl = this.dataset.pincodeUrl;
      this.storageKey = this.dataset.storageKey || 'delivery-options-pincode';
      this.nonServiceableNote = this.dataset.nonServiceableNote || 'Non serviceable pincode';

      this.input?.addEventListener('input', () => {
        this.manualOverride = true;
        this.input.value = normalizePincode(this.input.value);
        this.clearValidation();
        if (this.input.value.length === 6) {
          this.checkPincode(this.input.value, false);
        }
      });

      this.load();
    }

    async load() {
      this.setBusy(true);

      try {
        this.pincodeZones = await loadPincodeData(this.pincodeUrl);
      } catch (error) {
        this.showError('Unable to load delivery data.');
        this.setBusy(false);
        return;
      }

      this.setBusy(false);

      const storedPincode = normalizePincode(getStorageValue(this.storageKey));
      if (storedPincode.length === 6) {
        this.input.value = storedPincode;
        this.checkPincode(storedPincode, false);
        return;
      }

      const detectedLocation = await fetchPostalFromIp();

      if (detectedLocation?.postal && !this.manualOverride) {
        this.input.value = detectedLocation.postal;
        this.checkPincode(detectedLocation.postal, true, detectedLocation);
        return;
      }
    }

    setBusy(isBusy) {
      this.loading = isBusy;
    }

    clearValidation() {
      if (this.validation) {
        this.validation.classList.remove('is-visible');
        this.validation.innerHTML = '';
      }
    }

    showValidation() {
      if (this.validation) {
        this.validation.classList.add('is-visible');
        this.validation.innerHTML = renderCheckIcon();
      }
    }

    showError(message) {
      this.result.className = 'product-delivery-options__delivery is-error';
      this.result.textContent = message;
      this.clearValidation();
    }

    checkPincode(rawValue, isAuto = false, detectedLocation = null) {
      const pincode = normalizePincode(rawValue);

      if (pincode.length !== 6) {
        this.showError('Enter a 6 digit pincode.');
        return;
      }

      const zoneName = findZone(pincode, this.pincodeZones || {});
      if (!zoneName) {
        this.result.className = 'product-delivery-options__delivery is-error';
        this.result.textContent = this.nonServiceableNote;
        if (this.input) this.input.value = pincode;
        this.clearValidation();
        return;
      }

      const rule = getDeliveryRule(pincode, zoneName);
      const deliveryDate = formatIndiaDate(addIndiaDays(rule.days));

      if (this.input) this.input.value = pincode;
      setStorageValue(this.storageKey, pincode);
      this.showValidation();
      this.result.className = 'product-delivery-options__delivery is-success';
      this.result.innerHTML = renderTruckLine(`Get it by ${deliveryDate}`);
    }
  }

  customElements.define('delivery-options', DeliveryOptions);
}
